import { useState, useEffect } from 'react';
import { Modal, Box, SpaceBetween, Button, Alert } from '@amzn/awsui-components-react';
import { useTranslation } from 'react-i18next';
import {
  type GetParticipationsByEventAndAttendeesCommandOutput,
  type IdentifierType,
  type Event,
  type Shift,
} from '@amzn/red-velvet-api';
import { BulkUploadAddAttendeesContent, ModalTileOption } from './addAttendeesContent';
import { BulkUploadValidateAttendeesContent } from './validateAttendeesContent';
import { useAttendeeSessionStorageNonReactive } from '../../../hooks/attendance/attendeeSessionStorage';
import { publishKatalMetric } from '../../katalAnalytics';
import { useBulkParticipantSessionStorage } from '../../../hooks/attendance/bulkParticipantSessionStorage';

interface BulkUploadModalProps {
  visible: boolean;
  onDismiss: () => void;
  event: Event;
  shifts: Shift[];
  shiftId?: string;
}

type ModalStep = 'add' | 'validate';

export function BulkUploadModal({
  visible,
  onDismiss,
  event,
  shifts,
  shiftId,
}: BulkUploadModalProps) {
  const namespaces = ['attendance', 'translation'];
  const { t } = useTranslation(namespaces);

  const [currentStep, setCurrentStep] = useState<ModalStep>('add');
  const [showDismissAlert, setShowDismissAlert] = useState(false);
  const [validationData, setValidationData] =
    useState<GetParticipationsByEventAndAttendeesCommandOutput | null>(null);
  const [identifierType, setIdentifierType] = useState<IdentifierType | null>(null);
  const { clearAttendees } = useAttendeeSessionStorageNonReactive();
  const { bulkParticipants } = useBulkParticipantSessionStorage();

  useEffect(() => {
    if (visible) {
      setCurrentStep('add');
      setValidationData(null);
      setIdentifierType(null);
      setShowDismissAlert(false);
      clearAttendees();
    }
  }, [visible, clearAttendees]);

  const handleDismissRequest = (): void => {
    setShowDismissAlert(true);
  };

  const handleConfirmDismiss = (): void => {
    setShowDismissAlert(false);
    setCurrentStep('add');
    setValidationData(null);
    setIdentifierType(null);
    onDismiss();
    publishKatalMetric('attendance_bulkUploadModalDismissed', '');
  };

  const handleDoneInSearchAdd = (): void => {
    setShowDismissAlert(false);
    setCurrentStep('add');
    setValidationData(null);
    setIdentifierType(null);
    onDismiss();
    publishKatalMetric(
      `attendance_bulkUploadSearchAddValidationSuccessCount`,
      Object.keys(bulkParticipants).length.toString()
    );
  };

  const handleCancelDismiss = (): void => {
    setShowDismissAlert(false);
  };

  const handleValidationSuccess = (
    data: GetParticipationsByEventAndAttendeesCommandOutput,
    type: IdentifierType,
    tileOption: ModalTileOption
  ): void => {
    setValidationData(data);
    setIdentifierType(type);

    // for bulk add option, we want to move to Validation Summary step
    if (tileOption === 'bulk') {
      setCurrentStep('validate');
    }
    // for search add option, we move immediately to the Track attendance wizard
    if (tileOption === 'search') {
      handleDoneInSearchAdd();
    }
  };

  const handleGoBack = (): void => {
    setCurrentStep('add');
  };

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismissRequest}
      size="max"
      header={t('bulkUpload.header')}
    >
      <div className="bulk-upload-modal-container">
        {showDismissAlert && (
          <Box margin={{ bottom: 's' }}>
            <Alert
              type="warning"
              dismissible
              onDismiss={handleCancelDismiss}
              header={t('bulkUpload.dismissAlert.title')}
              action={
                <SpaceBetween direction="horizontal" size="xs">
                  <Button onClick={handleCancelDismiss}>
                    {t('bulkUpload.dismissAlert.cancel')}
                  </Button>
                  <Button variant="primary" onClick={handleConfirmDismiss}>
                    {t('bulkUpload.dismissAlert.confirm')}
                  </Button>
                </SpaceBetween>
              }
            >
              {t('bulkUpload.dismissAlert.message')}
            </Alert>
          </Box>
        )}

        {currentStep === 'add' && (
          <BulkUploadAddAttendeesContent
            event={event}
            shiftId={shiftId}
            shifts={shifts}
            isModalOpen={visible}
            onCancel={handleDismissRequest}
            onValidationSuccess={handleValidationSuccess}
          />
        )}

        {currentStep === 'validate' && validationData && identifierType && (
          <BulkUploadValidateAttendeesContent
            validationData={validationData}
            identifierType={identifierType}
            event={event}
            shifts={shiftId ? shifts.filter((shift) => shift.shiftId === shiftId) : shifts} // in single shift tracking, we always assign this single shift to all attendees
            shiftIdFromTrackAttendanceWizard={shiftId}
            onCancel={handleDismissRequest}
            onPrevious={handleGoBack}
            onComplete={handleConfirmDismiss}
          />
        )}
      </div>
    </Modal>
  );
}
